import type { ReactNode } from 'react'
import { Icon } from '@/components/Icon'
import { Tooltip } from '@/components/Tooltip'
import { QuestionIcon } from '@/features/legends/questions/components/QuestionIcon'
import type { QuestionType } from '@/features/legends/questions/question-types'
import styles from './PropertyInspector.module.css'

export interface PropertyInspectorTitleProps {
  type: QuestionType
  friendlyType: string
  /**
   * The ID field, for questions that have an ID.
   */
  children?: ReactNode
}

/**
 * The inspector's title: the question's type over its ID.
 */
export function PropertyInspectorTitle({
  type,
  friendlyType,
  children,
}: PropertyInspectorTitleProps) {
  return (
    <div className={styles.title}>
      <div className={styles.type}>
        <QuestionIcon type={type} size={16} />
        <span>{friendlyType}</span>
      </div>
      {children && <div className={styles.id}>{children}</div>}
    </div>
  )
}

export interface PropertyInspectorNoteProps {
  children?: ReactNode
}

/**
 * Explains a tab of the inspector.
 */
export function PropertyInspectorNote({
  children,
}: PropertyInspectorNoteProps) {
  return <p className={styles.note}>{children}</p>
}

export interface PropertyInspectorSectionTitleProps {
  title: string
  /**
   * Marks the section, with the error as the marker's tooltip.
   */
  error?: string
}

/**
 * The title of an expandable row, like an option's effect.
 */
export function PropertyInspectorSectionTitle({
  title,
  error,
}: PropertyInspectorSectionTitleProps) {
  return (
    <span className={styles.sectionTitle}>
      {title}
      {error && (
        <Tooltip content={error}>
          <span className={styles.sectionError}>
            <Icon name="status-alert" size={14} aria-hidden="true" />
            <span className="sr-only">{error}</span>
          </span>
        </Tooltip>
      )}
    </span>
  )
}
